import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCcw, Send, CheckCircle2 } from "lucide-react";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function CheckoutFailuresPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/checkout-failures", { params: { limit: 50 } });
      setData(data);
    } catch { /* ignored */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendRetry = async (row) => {
    if (!row.user_email) {
      toast.error("No customer email on this row");
      return;
    }
    const already = row.retry_email_sent_at;
    if (already && !window.confirm(`Already sent to ${row.user_email} on ${fmt(already)}. Send again?`)) return;
    setSendingId(row.id);
    try {
      const { data } = await api.post(`/admin/checkout-failures/${row.id}/retry-email`, {
        site_url: window.location.origin,
      });
      if (data?.ok) {
        toast.success(`Sent retry email to ${data.sent_to}`);
        await load();
      } else {
        toast.error("Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingId(null);
    }
  };

  const total = data?.total ?? 0;
  const rows = data?.recent ?? [];

  return (
    <div className="bg-ink border border-white/10 p-6" data-testid="checkout-failures-panel">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={18} className="text-hotpink" />
        <h2 className="brush-title text-3xl text-white">Checkout Failures</h2>
        <span className="ml-auto text-white/50 text-sm font-outfit">
          {total} total
        </span>
        <button
          onClick={load}
          className="bg-white/10 hover:bg-white/20 text-white px-2 py-1 font-outfit text-xs flex items-center gap-1"
          data-testid="checkout-failures-refresh"
        >
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="scribble text-white/60 text-sm mb-4">
        Every time a Stripe checkout session fails to open, it lands here with the exact error &mdash; so you never have to guess. Hit &ldquo;Retry Email&rdquo; to send the customer a personalized apology + direct link back.
      </p>
      {rows.length === 0 ? (
        <div className="text-white/40 font-outfit text-sm py-6 text-center border border-dashed border-white/10">
          No checkout failures. Nice.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((r) => {
            const sent = r.retry_email_sent_at;
            const isSending = sendingId === r.id;
            return (
              <div key={r.id} className="p-3 bg-coal font-outfit text-sm" data-testid={`checkout-failure-${r.id}`}>
                <div className="flex justify-between items-baseline gap-3">
                  <div className="text-white font-bold truncate">{r.user_email || "(unknown user)"}</div>
                  <div className="text-white/40 text-xs whitespace-nowrap">{fmt(r.created_at)}</div>
                </div>
                <div className="flex gap-2 mt-1 flex-wrap items-baseline">
                  <span className="bg-hotpink/20 text-hotpink px-2 py-0.5 text-xs font-bold uppercase tracking-wider">
                    {r.kind}
                  </span>
                  {r.extra?.episode_number != null && (
                    <span className="text-white/70 text-xs">Ep {r.extra.episode_number}</span>
                  )}
                  {Array.isArray(r.extra?.episode_numbers) && (
                    <span className="text-white/70 text-xs">
                      Eps {r.extra.episode_numbers.join(", ")}
                    </span>
                  )}
                  {r.extra?.amount != null && (
                    <span className="text-white/70 text-xs">${r.extra.amount}</span>
                  )}
                  {r.extra?.stripe_key_mode && (
                    <span className={`text-xs font-bold uppercase ${r.extra.stripe_key_mode === "test" ? "text-yellow-400" : r.extra.stripe_key_mode === "live" ? "text-green-400" : "text-red-400"}`}>
                      {r.extra.stripe_key_mode}
                    </span>
                  )}
                </div>
                <div className="text-red-300 text-xs mt-1 font-mono break-all">
                  {r.error}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  {r.user_email && (
                    <button
                      onClick={() => sendRetry(r)}
                      disabled={isSending}
                      className={`px-3 py-1 font-outfit font-bold uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-50 ${
                        sent ? "bg-white/10 hover:bg-white/20 text-white/70" : "bg-hotpink hover:bg-hotpink/90 text-white"
                      }`}
                      data-testid={`retry-email-${r.id}`}
                      title={sent ? `Sent ${fmt(sent)}` : "Send apology + direct checkout link"}
                    >
                      {sent ? <CheckCircle2 size={12} /> : <Send size={12} />}
                      {isSending ? "Sending…" : sent ? "Resend" : "Retry Email"}
                    </button>
                  )}
                  {sent && (
                    <span className="text-white/40 text-xs">sent {fmt(sent)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
